import { useState } from 'react';

import { useCrmDashboard, useCustomers, useOrgUsers } from '../api/hooks';
import type { DealStage } from '../api/types';
import { FormField, Select, Tabs, TextInput } from '../components/DesignSystem';
import { cx } from '../components/cx';
import { PageHeader } from '../components/PageHeader';
import { CrmDashboardTab } from './crm/CrmDashboardTab';
import { CustomersTab } from './crm/CustomersTab';
import { PipelineTab } from './crm/PipelineTab';
import type { CrmFilters } from './crm/PipelineTab';
import { STAGE_LABELS } from './crm/stages';

type TabKey = 'dashboard' | 'pipeline' | 'customers' | 'pitches' | 'communications' | 'reports';

const COMING_SOON_TABS: ReadonlySet<string> = new Set(['pitches', 'communications', 'reports']);

const TAB_ITEMS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'customers', label: 'Customers' },
  { key: 'pitches', label: 'Pitches' },
  { key: 'communications', label: 'Communications' },
  { key: 'reports', label: 'Reports' },
];

const EMPTY_FILTERS: CrmFilters = { search: '', customerId: '', salesExecId: '', stage: '' };

/** `/crm` — sales pipeline, customers, and performance dashboard. */
export function CrmPage() {
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [filters, setFilters] = useState<CrmFilters>(EMPTY_FILTERS);

  const dashboard = useCrmDashboard();
  const customers = useCustomers({ limit: 100 });
  // Requires the users.manage permission — non-admins simply get no options.
  const orgUsers = useOrgUsers({ limit: 100 });

  const salesExecs = orgUsers.data?.data ?? [];
  const funnel = dashboard.data?.funnel ?? [];

  const set = (patch: Partial<CrmFilters>) => setFilters((current) => ({ ...current, ...patch }));

  return (
    <>
      <PageHeader
        caption="Track buyers, pitches, and licensing deals from first contact to signature."
        title="Sales CRM"
      />

      <section className="rfp-card">
        <div className="rfp-filter-grid">
          <FormField htmlFor="crm-search" label="Search">
            <TextInput
              id="crm-search"
              onChange={(event) => set({ search: event.target.value })}
              placeholder="Search deals..."
              value={filters.search}
            />
          </FormField>
          <FormField htmlFor="crm-customer" label="Customer">
            <Select
              id="crm-customer"
              onChange={(event) => set({ customerId: event.target.value })}
              value={filters.customerId}
            >
              <option value="">All customers</option>
              {(customers.data?.data ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField htmlFor="crm-exec" label="Sales Executive">
            <Select
              id="crm-exec"
              onChange={(event) => set({ salesExecId: event.target.value })}
              value={filters.salesExecId}
            >
              <option value="">All executives</option>
              {salesExecs.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField htmlFor="crm-currency" label="Reporting Currency">
            <Select disabled id="crm-currency" title="GBP only for now" value="GBP">
              <option value="GBP">GBP</option>
            </Select>
          </FormField>
        </div>

        <div className="rfp-chip-row">
          {funnel.map((entry) => (
            <button
              className={cx('rfp-chip', filters.stage === entry.stage && 'rfp-chip--active')}
              key={entry.stage}
              onClick={() =>
                set({ stage: filters.stage === entry.stage ? '' : (entry.stage as DealStage) })
              }
              type="button"
            >
              {STAGE_LABELS[entry.stage]}
              <span>({entry.count})</span>
            </button>
          ))}
        </div>
      </section>

      <div className="rfp-toolbar" title="Pitches, Communications and Reports are coming soon">
        <Tabs
          active={tab}
          items={TAB_ITEMS.map((item) => ({ ...item, disabled: COMING_SOON_TABS.has(item.key) }))}
          onChange={(key) => {
            // Placeholder tabs render but stay inert until those modules ship.
            if (!COMING_SOON_TABS.has(key)) {
              setTab(key as TabKey);
            }
          }}
        />
      </div>

      {tab === 'dashboard' && (
        <CrmDashboardTab dashboard={dashboard.data} isLoading={dashboard.isLoading} />
      )}
      {tab === 'pipeline' && <PipelineTab filters={filters} salesExecs={salesExecs} />}
      {tab === 'customers' && <CustomersTab search={filters.search} />}
    </>
  );
}
